import type { LogicalType } from '@/types'
import type { DataTypeDefinition, DialectAdapter, DialectWarning } from '../dialectTypes'

const DATA_TYPES: DataTypeDefinition[] = [
  { value: 'NUMBER(5)',      label: 'NUMBER(5) (smallint)',  logicalType: 'smallint' },
  { value: 'NUMBER(10)',     label: 'NUMBER(10) (integer)',  logicalType: 'integer' },
  { value: 'NUMBER(19)',     label: 'NUMBER(19) (bigint)',   logicalType: 'bigint' },
  { value: 'NUMBER(10,2)',   label: 'NUMBER(p,s)',           logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'DECIMAL(10,2)',  label: 'DECIMAL(p,s)',          logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'NUMERIC(10,2)',  label: 'NUMERIC(p,s)',          logicalType: 'decimal', hasPrecisionScale: true },
  { value: 'BINARY_FLOAT',  label: 'BINARY_FLOAT',          logicalType: 'float' },
  { value: 'BINARY_DOUBLE', label: 'BINARY_DOUBLE',         logicalType: 'float' },
  { value: 'VARCHAR2(255)',  label: 'VARCHAR2(n)',            logicalType: 'string', hasLength: true },
  { value: 'NVARCHAR2(255)',label: 'NVARCHAR2(n)',            logicalType: 'string', hasLength: true },
  { value: 'CHAR(1)',        label: 'CHAR(n)',               logicalType: 'string', hasLength: true },
  { value: 'CLOB',          label: 'CLOB',                  logicalType: 'text' },
  { value: 'NCLOB',         label: 'NCLOB',                 logicalType: 'text' },
  { value: 'NUMBER(1)',     label: 'NUMBER(1) (boolean)',    logicalType: 'boolean' },
  { value: 'DATE',          label: 'DATE',                  logicalType: 'date' },
  { value: 'TIMESTAMP',     label: 'TIMESTAMP',             logicalType: 'datetime' },
  { value: 'TIMESTAMP WITH TIME ZONE', label: 'TIMESTAMP WITH TIME ZONE', logicalType: 'datetime' },
  { value: 'RAW(16)',       label: 'RAW(16) (UUID)',         logicalType: 'uuid' },
  { value: 'CLOB',          label: 'CLOB (JSON)',            logicalType: 'json' },
  { value: 'BLOB',          label: 'BLOB',                  logicalType: 'binary' },
  { value: 'RAW',           label: 'RAW',                   logicalType: 'binary' },
]

const LOGICAL_TO_PHYSICAL: Record<LogicalType, string> = {
  string:   'VARCHAR2(255)',
  text:     'CLOB',
  integer:  'NUMBER(10)',
  bigint:   'NUMBER(19)',
  smallint: 'NUMBER(5)',
  decimal:  'NUMBER(10,2)',
  float:    'BINARY_DOUBLE',
  boolean:  'NUMBER(1)',
  date:     'DATE',
  time:     'TIMESTAMP',
  datetime: 'TIMESTAMP WITH TIME ZONE',
  uuid:     'RAW(16)',
  json:     'CLOB',
  binary:   'BLOB',
}

function normalizeTypeKey(type: string): string {
  return type.trim().toUpperCase().replace(/\s*\(.*\)/, '').replace(/\s+/g, ' ')
}

export const oracleDialect: DialectAdapter = {
  id: 'oracle',
  label: 'Oracle',
  dataTypes: DATA_TYPES,

  mapLogicalToPhysical(type) {
    return LOGICAL_TO_PHYSICAL[type] ?? 'CLOB'
  },

  mapPhysicalToLogical(type) {
    const key = normalizeTypeKey(type)
    switch (key) {
      case 'NUMBER':
        return 'decimal'
      case 'BINARY_FLOAT':
      case 'FLOAT':
        return 'float'
      case 'BINARY_DOUBLE':
        return 'float'
      case 'VARCHAR2':
      case 'NVARCHAR2':
      case 'CHAR':
      case 'NCHAR':
        return 'string'
      case 'CLOB':
      case 'NCLOB':
      case 'LONG':
        return 'text'
      case 'DATE':
        return 'date'
      case 'TIMESTAMP':
        return 'datetime'
      case 'RAW':
        return 'binary'
      case 'BLOB':
        return 'binary'
      default:
        return 'text'
    }
  },

  validateColumn(column) {
    const warnings: DialectWarning[] = []
    const upper = column.type.trim().toUpperCase()
    if (upper === 'BOOLEAN' || upper === 'BOOL') {
      warnings.push({ column: column.name, message: 'BOOLEAN is not an Oracle type; use NUMBER(1).', severity: 'warning' })
    }
    if (upper === 'UUID') {
      warnings.push({ column: column.name, message: 'UUID is not an Oracle type; use RAW(16).', severity: 'warning' })
    }
    if (upper === 'VARCHAR' || upper.startsWith('VARCHAR(')) {
      warnings.push({ column: column.name, message: 'Use VARCHAR2 instead of VARCHAR in Oracle.', severity: 'info' })
    }
    return warnings
  },
}
